import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Spin, Tag, Space, Result } from 'antd';
import { LoginOutlined, UserAddOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';

const { Title, Text, Paragraph } = Typography;

const TYPE_LABELS = {
    context: 'Context',
    relationship: 'Relationship',
};

function InvitePage() {
    const api = useApi();
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, keycloak, user } = useAuth();
    const [entity, setEntity] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        api.get(`/api/entities/by-invite/${token}`)
            .then(data => { if (data) setEntity(data); })
            .catch(() => setNotFound(true))
            .finally(() => setFetching(false));
    }, [token]);

    // If already logged in, accept the invite (lifts the pending-registration
    // gate — see accept_invite in routers/entities.py) and send straight to
    // the entity page. Best-effort: navigation doesn't wait on the accept
    // call, since viewing the entity page itself doesn't require approval;
    // `accepted` just guards against re-firing on re-render.
    useEffect(() => {
        if (isLoading || !isAuthenticated || !entity || !user?.id || accepted) return;
        setAccepted(true);
        api.post(`/api/entities/by-invite/${token}/accept`, undefined, { params: { user_id: user.id } }).catch(() => {});
        navigate(`/entity/${entity.id}`, { replace: true });
    }, [isLoading, isAuthenticated, entity, user, accepted, token, navigate, api]);

    if (fetching || isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (notFound) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Result status="404" title="Invite link not found" subTitle="This link may have expired or been revoked." />
            </div>
        );
    }

    const redirectUri = window.location.href;

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '100vh', padding: 24, background: 'var(--bg-primary)',
        }}>
            <div style={{ marginBottom: 32, textAlign: 'center' }}>
                <img src="/full_color.svg" alt="Vellby" style={{ height: 28 }} />
            </div>
            <Card
                style={{ width: '100%', maxWidth: 480, borderRadius: 10 }}
                styles={{ body: { padding: '36px 32px' } }}
            >
                <Space direction="vertical" size={20} style={{ width: '100%' }}>
                    <div>
                        <Tag color="blue" style={{ marginBottom: 12 }}>
                            {TYPE_LABELS[entity.entity_type] || entity.entity_type}
                        </Tag>
                        <Title level={3} style={{ margin: 0 }}>{entity.name}</Title>
                        {entity.description && (
                            <Paragraph type="secondary" style={{ marginTop: 10, marginBottom: 0 }}>
                                {entity.description}
                            </Paragraph>
                        )}
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 20 }}>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>
                            You've been invited to join
                        </Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Create a Vellby account to request membership, or log in if you already have one.
                        </Text>
                    </div>

                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            size="large"
                            block
                            onClick={() => keycloak.register({ redirectUri })}
                        >
                            Create an account
                        </Button>
                        <Button
                            icon={<LoginOutlined />}
                            size="large"
                            block
                            onClick={() => keycloak.login({ redirectUri })}
                        >
                            Log in
                        </Button>
                    </Space>
                </Space>
            </Card>
        </div>
    );
}

export default InvitePage;
